import { useState, useEffect } from 'react';
import {
  X,
  Download,
  ZoomIn,
  ZoomOut,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  AlertCircle
} from 'lucide-react';
import { IDocument } from '../../models/document';
import { useDocumentToken, getFileType, getFileName } from './useDocumentToken';
import './DocumentViewer.css';

interface DocumentViewerProps {
  document: IDocument | null;
  documents?: IDocument[]; // Pour la navigation entre documents
  isOpen: boolean;
  onClose: () => void;
  onDownload?: (doc: IDocument) => Promise<void>;
  onNavigate?: (doc: IDocument) => void;
  title?: string;
  /**
   * Function to get the token URL for a document.
   * This allows each context (SAV, Contract, etc.) to provide its own endpoint.
   * Example: (docId) => `/sav-v2/${savId}/documents/${docId}/get-download-token`
   */
  getTokenUrl: (documentId: number) => string;
  /**
   * Display mode for multiple documents:
   * - false (default): Show prev/next navigation buttons
   * - true: Show sidebar list for navigation
   */
  listMode?: boolean;
  /**
   * Custom render function for list items (only used when listMode=true)
   * If not provided, displays default item with document label
   */
  renderListItem?: (doc: IDocument, isActive: boolean) => React.ReactNode;
}

export default function DocumentViewer({
  document,
  documents = [],
  isOpen,
  onClose,
  onDownload,
  onNavigate,
  title,
  getTokenUrl,
  listMode = false,
  renderListItem
}: DocumentViewerProps) {
  const [zoom, setZoom] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Use the shared hook for token management
  const { viewUrl, loading, error, refresh } = useDocumentToken({
    document,
    getTokenUrl,
    enabled: isOpen
  });

  // Déterminer l'index du document actuel dans la liste
  const currentIndex = document ? documents.findIndex(d => d.id === document.id) : -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < documents.length - 1;

  // Use utility functions from the hook
  const fileName = document ? getFileName(document) : '';
  const fileType = document ? getFileType(fileName) : 'other';

  // Gestion du plein écran
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      window.document.documentElement.requestFullscreen?.();
    } else {
      window.document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Navigation
  const handlePrevious = () => {
    if (hasPrevious && onNavigate) {
      onNavigate(documents[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext && onNavigate) {
      onNavigate(documents[currentIndex + 1]);
    }
  };

  // Zoom
  const handleZoomIn = () => setZoom(Math.min(zoom + 25, 200));
  const handleZoomOut = () => setZoom(Math.max(zoom - 25, 50));
  const resetZoom = () => setZoom(100);

  // Gestion des raccourcis clavier
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case '+':
        case '=':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleZoomIn();
          }
          break;
        case '-':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleZoomOut();
          }
          break;
        case '0':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            resetZoom();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentIndex, zoom]);

  if (!isOpen || !document) return null;

  const displayName = getFileName(document);

  return (
    <>
      {/* Overlay */}
      <div
        className="document-viewer-overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="document-viewer-modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="document-viewer-header">
          <div className="document-viewer-title">
            <FileText size={20} />
            <span>{title || displayName}</span>
            {documents.length > 1 && (
              <span className="document-viewer-counter">
                ({currentIndex + 1} / {documents.length})
              </span>
            )}
          </div>

          <div className="document-viewer-actions">
            {/* Navigation - only show prev/next in non-list mode */}
            {documents.length > 1 && !listMode && (
              <>
                <button
                  className="document-viewer-btn"
                  onClick={handlePrevious}
                  disabled={!hasPrevious}
                  title="Document précédent (←)"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  className="document-viewer-btn"
                  onClick={handleNext}
                  disabled={!hasNext}
                  title="Document suivant (→)"
                >
                  <ChevronRight size={20} />
                </button>
                <div className="document-viewer-separator" />
              </>
            )}

            {/* Zoom controls */}
            {fileType !== 'other' && !loading && !error && (
              <>
                <button
                  className="document-viewer-btn"
                  onClick={handleZoomOut}
                  disabled={zoom <= 50}
                  title="Zoom arrière (-)"
                >
                  <ZoomOut size={20} />
                </button>
                <span className="document-viewer-zoom">{zoom}%</span>
                <button
                  className="document-viewer-btn"
                  onClick={handleZoomIn}
                  disabled={zoom >= 200}
                  title="Zoom avant (+)"
                >
                  <ZoomIn size={20} />
                </button>
                <div className="document-viewer-separator" />
              </>
            )}

            {/* Actions */}
            {onDownload && (
              <button
                className="document-viewer-btn document-viewer-btn-primary"
                onClick={() => onDownload(document)}
                title="Télécharger"
              >
                <Download size={20} />
              </button>
            )}
            <button
              className="document-viewer-btn"
              onClick={toggleFullscreen}
              title="Plein écran"
            >
              <Maximize2 size={20} />
            </button>
            <button
              className="document-viewer-btn document-viewer-btn-close"
              onClick={onClose}
              title="Fermer (ESC)"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        {listMode && documents.length > 1 ? (
          <div className="document-viewer-layout-with-sidebar">
            {/* Sidebar with document list */}
            <div className="document-viewer-sidebar">
              {documents.map((doc) => {
                const isActive = doc.id === document?.id;
                return (
                  <div
                    key={doc.id}
                    className={`document-viewer-list-item ${isActive ? 'active' : ''}`}
                    onClick={() => onNavigate?.(doc)}
                  >
                    {renderListItem ? (
                      renderListItem(doc, isActive)
                    ) : (
                      <div className="document-viewer-list-item-default">
                        <FileText size={16} className="tw-flex-shrink-0" />
                        <span className="tw-truncate">{getFileName(doc)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Main content area */}
            <div className="document-viewer-main-content">
              {loading && (
                <div className="document-viewer-loading">
                  <Loader2 size={40} className="tw-animate-spin" />
                  <p>Chargement du document...</p>
                </div>
              )}

              {error && (
                <div className="document-viewer-error">
                  <AlertCircle size={40} />
                  <p>{error}</p>
                  <div className="tw-flex tw-gap-2 tw-mt-4">
                    <button
                      className="document-viewer-btn document-viewer-btn-primary"
                      onClick={refresh}
                    >
                      Réessayer
                    </button>
                    {onDownload && (
                      <button
                        className="document-viewer-btn document-viewer-btn-primary"
                        onClick={() => onDownload(document)}
                      >
                        <Download size={20} />
                        Télécharger le document
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!loading && !error && viewUrl && (
                <>
                  {fileType === 'image' ? (
                    <div className="document-viewer-image-container">
                      <img
                        src={viewUrl}
                        alt={displayName}
                        style={{ transform: `scale(${zoom / 100})` }}
                        className="document-viewer-image"
                      />
                    </div>
                  ) : (
                    <iframe
                      src={viewUrl}
                      title={displayName}
                      className="document-viewer-iframe"
                      style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="document-viewer-content">
            {loading && (
              <div className="document-viewer-loading">
                <Loader2 size={40} className="tw-animate-spin" />
                <p>Chargement du document...</p>
              </div>
            )}

            {error && (
              <div className="document-viewer-error">
                <AlertCircle size={40} />
                <p>{error}</p>
                <div className="tw-flex tw-gap-2 tw-mt-4">
                  <button
                    className="document-viewer-btn document-viewer-btn-primary"
                    onClick={refresh}
                  >
                    Réessayer
                  </button>
                  {onDownload && (
                    <button
                      className="document-viewer-btn document-viewer-btn-primary"
                      onClick={() => onDownload(document)}
                    >
                      <Download size={20} />
                      Télécharger le document
                    </button>
                  )}
                </div>
              </div>
            )}

            {!loading && !error && viewUrl && (
              <>
                {fileType === 'image' ? (
                  <div className="document-viewer-image-container">
                    <img
                      src={viewUrl}
                      alt={displayName}
                      style={{ transform: `scale(${zoom / 100})` }}
                      className="document-viewer-image"
                    />
                  </div>
                ) : (
                  <iframe
                    src={viewUrl}
                    title={displayName}
                    className="document-viewer-iframe"
                    style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* Footer avec info */}
        <div className="document-viewer-footer">
          <span className="document-viewer-filename-footer">{displayName}</span>
          <div className="document-viewer-shortcuts">
            <span>ESC: Fermer</span>
            {documents.length > 1 && <span>← →: Naviguer</span>}
            {fileType !== 'other' && <span>Ctrl +/-: Zoom</span>}
          </div>
        </div>
      </div>
    </>
  );
}